import React, { useEffect, useMemo, useState } from "react";
import { ChevronRight, ChevronDown, Search, X } from "lucide-react";

export type OrgUnitOption = {
  name: string;
  level: number;
  leaf?: number | boolean;
  l1name?: string;
  l2name?: string;
  l3name?: string;
  l4name?: string;
  l5name?: string;
  l6name?: string;
  l7name?: string;
};

export type OrgUnitHierarchy = {
  moh: string;
  region: string;
  zone: string;
  woreda: string;
  phcu: string;
  level6: string;
  level7: string;
};

type OrgUnitNode = {
  id: string;
  name: string;
  level: number;
  children: OrgUnitNode[];
};

type OrgUnitTreeProps = {
  orgUnits: OrgUnitOption[];
  selected: OrgUnitHierarchy;
  onSelect: (hierarchy: OrgUnitHierarchy) => void;
  defaultExpandedDepth?: number;
};

const EMPTY_HIERARCHY: OrgUnitHierarchy = { moh: "", region: "", zone: "", woreda: "", phcu: "", level6: "", level7: "" };

const matchesOrgUnit = (candidate: string | undefined, query: string) => {
  const value = String(candidate || "").trim().toLowerCase();
  const needle = query.trim().toLowerCase();
  return !needle || value === needle;
};

const getLName = (o: OrgUnitOption, level: number): string =>
  String((o as any)[`l${level}name`] || "");

const buildTree = (orgUnits: OrgUnitOption[]): OrgUnitNode[] => {
  const level1 = Array.from(
    new Set(
      orgUnits
        .filter((o) => o.level === 1)
        .map((o) => o.name)
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const childrenFor = (
    items: OrgUnitOption[],
    parentLevel: number,
    parentName: string,
    idPrefix: string,
  ): OrgUnitNode[] => {
    const childLevel = parentLevel + 1;
    if (childLevel > 7) return [];

    const branchItems = items.filter((o) => matchesOrgUnit(getLName(o, parentLevel), parentName));

    const childNames = Array.from(
      new Set(
        branchItems
          .filter((o) => o.level >= childLevel)
          .map((o) => getLName(o, childLevel) || o.name)
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return childNames.map((name) => ({
      id: `${idPrefix}-${name}`,
      name,
      level: childLevel,
      children: childrenFor(branchItems, childLevel, name, `${idPrefix}-${name}`),
    }));
  };

  return level1.map((name) => ({
    id: `moh-${name}`,
    name,
    level: 1,
    children: childrenFor(orgUnits, 1, name, `moh-${name}`),
  }));
};

const collectExpandableNodeIds = (nodes: OrgUnitNode[], maxDepth: number, depth = 0): string[] =>
  nodes.flatMap((node) => [
    ...(node.children.length > 0 && depth < maxDepth ? [node.id] : []),
    ...collectExpandableNodeIds(node.children, maxDepth, depth + 1),
  ]);

export const OrgUnitTree: React.FC<OrgUnitTreeProps> = React.memo(({ orgUnits, selected, onSelect, defaultExpandedDepth }) => {
  const tree = useMemo(() => buildTree(orgUnits), [orgUnits]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (defaultExpandedDepth !== undefined && tree.length > 0) {
      setExpanded(new Set(collectExpandableNodeIds(tree, defaultExpandedDepth)));
    }
  }, [defaultExpandedDepth, tree]);

  const nodeOrDescendantMatches = (node: OrgUnitNode, needle: string): boolean => {
    if (!needle) return true;
    const lower = needle.toLowerCase();
    if (node.name.toLowerCase().includes(lower)) return true;
    return node.children.some((child) => nodeOrDescendantMatches(child, needle));
  };

  const filterTree = (nodes: OrgUnitNode[], needle: string): OrgUnitNode[] => {
    if (!needle) return nodes;
    return nodes
      .filter((n) => nodeOrDescendantMatches(n, needle))
      .map((n) => ({ ...n, children: filterTree(n.children, needle) }));
  };

  const filteredTree = useMemo(() => filterTree(tree, searchQuery), [tree, searchQuery]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasMatchesAbove = (node: OrgUnitNode): boolean => {
    if (!searchQuery) return false;
    return nodeOrDescendantMatches(node, searchQuery);
  };

  const isAutoExpanded = (node: OrgUnitNode): boolean => {
    if (!searchQuery) return false;
    return hasMatchesAbove(node);
  };

  const renderNode = (node: OrgUnitNode, depth: number, parentHierarchy: string[]) => {
    const isExpanded = expanded.has(node.id) || isAutoExpanded(node);
    const hasChildren = node.children.length > 0;

    const isSelected = (() => {
      if (depth === 0) return selected.moh === node.name;
      if (depth === 1) return selected.region === node.name;
      if (depth === 2) return selected.zone === node.name;
      if (depth === 3) return selected.woreda === node.name;
      if (depth === 4) return selected.phcu === node.name;
      if (depth === 5) return selected.level6 === node.name;
      if (depth === 6) return selected.level7 === node.name;
      return false;
    })();

    const handleClick = () => {
      const newHierarchy = { ...EMPTY_HIERARCHY };
      if (depth >= 1) newHierarchy.moh = parentHierarchy[0] || "";
      if (depth >= 2) newHierarchy.region = parentHierarchy[1] || "";
      if (depth >= 3) newHierarchy.zone = parentHierarchy[2] || "";
      if (depth >= 4) newHierarchy.woreda = parentHierarchy[3] || "";
      if (depth >= 5) newHierarchy.phcu = parentHierarchy[4] || "";
      if (depth >= 6) newHierarchy.level6 = parentHierarchy[5] || "";
      if (depth === 0) newHierarchy.moh = node.name;
      else if (depth === 1) newHierarchy.region = node.name;
      else if (depth === 2) newHierarchy.zone = node.name;
      else if (depth === 3) newHierarchy.woreda = node.name;
      else if (depth === 4) newHierarchy.phcu = node.name;
      else if (depth === 5) newHierarchy.level6 = node.name;
      else if (depth === 6) newHierarchy.level7 = node.name;
      onSelect(newHierarchy);
    };

    return (
      <div key={node.id}>
        <div
          className={`flex cursor-pointer items-center gap-1 rounded-lg px-1.5 py-0.5 text-[10px] transition-all hover:bg-slate-100 ${
            isSelected ? "bg-primary-100 font-medium text-primary-700" : "text-slate-600"
          }`}
          style={{ paddingLeft: `${depth * 12 + 6}px` }}
        >
          {hasChildren ? (
            <span
              className="shrink-0 text-slate-400"
              onClick={(e) => {
                e.stopPropagation();
                toggle(node.id);
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </span>
          ) : (
            <span className="w-3 shrink-0" />
          )}
          <span className="truncate" onClick={handleClick} title={node.name}>
            {node.name}
          </span>
          {hasChildren && (
            <span
              className="ml-auto shrink-0 text-[9px] text-slate-400"
              onClick={(e) => {
                e.stopPropagation();
                toggle(node.id);
              }}
            >
              {node.children.length}
            </span>
          )}
        </div>
        {isExpanded && hasChildren && (
          <div>
            {node.children.map((child) => renderNode(child, depth + 1, [...parentHierarchy, node.name]))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="relative mb-1.5">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search org units..."
          className="input h-6 w-full pl-7 pr-6 text-[10px]"
        />
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      {[selected.moh, selected.region, selected.zone, selected.woreda, selected.phcu, selected.level6, selected.level7].filter(Boolean).length > 0 && (
        <div className="mb-1.5 flex items-center gap-1 rounded-lg bg-primary-100 px-2 py-1">
          <span className="flex-1 truncate text-[10px] font-medium text-primary-700">
            {[selected.moh, selected.region, selected.zone, selected.woreda, selected.phcu, selected.level6, selected.level7].filter(Boolean).join(" › ")}
          </span>
          <button type="button" onClick={() => onSelect(EMPTY_HIERARCHY)} className="shrink-0 text-primary-400 hover:text-primary-600">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="max-h-[200px] overflow-y-auto rounded-xl border border-slate-100 bg-white shadow-soft">
        {filteredTree.length === 0 ? (
          <div className="px-2 py-3 text-center text-[10px] text-slate-400">
            {searchQuery ? "No matching org units." : "No org units loaded."}
          </div>
        ) : (
          filteredTree.map((node) => renderNode(node, 0, []))
        )}
      </div>
    </div>
  );
});
OrgUnitTree.displayName = "OrgUnitTree";
